/** 管理端用量统计支持的消费类型。 */
export type UsageConsumptionType = 'FREE' | 'TRIAL' | 'COMMERCIAL';

/** 资源用量的计量类型。 */
export type UsageResourceType = 'COUNT' | 'CREDIT';

/** 账户额度状态。 */
export type UsageAccountQuotaStatus = 'LIMITED' | 'UNLIMITED' | 'NOT_CONFIGURED';

/** 账户额度展示分组。 */
export type UsageAccountQuotaGroup = 'LEGACY_COUNT' | 'UNIFIED_COUNT' | 'CREDIT';

/** 账户额度统计周期。 */
export type UsageAccountQuotaDimension = 'MONTH' | 'YEAR' | 'ALL';

/** 原始用量事件的操作类型。 */
export type UsageOperationType = 'DEDUCT' | 'REFUND';

/** 用量统计覆盖的业务任务分类。 */
export type UsageTaskCategory = 'FTO' | 'NOVELTY' | 'IMAGE_SEARCH';

/** 管理端用量统计的公共筛选条件。 */
export interface ApiUsageStatisticsCommonFilter {
    /** 查询开始时间，Unix 毫秒时间戳。 */
    start_time: number;
    /** 查询结束时间，Unix 毫秒时间戳。 */
    end_time: number;
    /** 消费类型筛选；不传或传空数组表示全部已知类型。 */
    consumption_types?: UsageConsumptionType[];
}

/** 管理端用量统计必须且只能指定一种查询主体。 */
export type ApiUsageStatisticsSubjectFilter =
    | { user_id: string; user_email?: never; company_id?: never }
    | { user_id?: never; user_email: string; company_id?: never }
    | { user_id?: never; user_email?: never; company_id: string };

/** 账户额度必须且只能指定用户 ID 或用户邮箱。 */
export type ApiUsageAccountQuotaRequest =
    | { user_id: string; user_email?: never }
    | { user_id?: never; user_email: string };

/** 管理端用量统计的完整筛选条件。 */
export type ApiUsageStatisticsFilter = ApiUsageStatisticsCommonFilter & ApiUsageStatisticsSubjectFilter;

/** 管理端用量明细的分页查询参数。 */
export type ApiUsageStatisticsDetailsRequest = ApiUsageStatisticsFilter & {
    /** 按归并后的任务分类精确筛选明细。 */
    task_category?: UsageTaskCategory;
    /** 按后端资源 Key 精确筛选明细。 */
    resource_name?: string;
    /** 页码，从 1 开始。 */
    page: number;
    /** 每页条数，最大 100。 */
    limit: number;
};

/** 当前明细表应用的卡片过滤。 */
export type UsageStatisticsDetailFilter =
    | { type: 'TASK_CATEGORY'; value: UsageTaskCategory }
    | { type: 'RESOURCE'; value: string };

/** 单个资源维度的用量汇总。 */
export interface UsageResourceSummary {
    /** 后端资源 Key，用作稳定标识。 */
    resource_name: string;
    /** 后端返回的资源展示名。 */
    resource_display_name: string;
    /** 资源计量类型。 */
    resource_type: UsageResourceType;
    /** 扣减减去返还后的净消耗。 */
    net_usage: number;
    /** 去重后的任务数量。 */
    task_count: number;
    /** 原始扣减或返还事件数量。 */
    detail_count: number;
    /** 去重后的涉及用户数量。 */
    user_count: number;
}

/** 单项实时账户额度。 */
export interface UsageAccountQuota {
    /** 后端资源 Key，用作稳定标识。 */
    resource_name: string;
    /** 后端返回的资源展示名。 */
    resource_display_name: string;
    /** 资源计量类型。 */
    resource_type: UsageResourceType;
    /** 额度展示分组。 */
    group: UsageAccountQuotaGroup;
    /** 额度统计周期。 */
    dimension: UsageAccountQuotaDimension;
    /** 有限、无限或未配置状态。 */
    status: UsageAccountQuotaStatus;
    /** 总额度；无限额度时为 `null`。 */
    total: number | null;
    /** 已使用额度。 */
    used: number;
    /** 剩余额度；无限额度时为 `null`。 */
    remaining: number | null;
}

/** 实时账户额度接口响应。 */
export interface ApiUsageAccountQuotaResponse {
    /** 固定顺序返回的九项账户额度。 */
    quotas: UsageAccountQuota[];
}

/** 单个任务分类维度的用量汇总。 */
export interface UsageTaskCategorySummary {
    /** 归并后的任务分类。 */
    task_category: UsageTaskCategory;
    /** 去重后的任务数量。 */
    task_count: number;
    /** 原始扣减或返还事件数量。 */
    detail_count: number;
    /** 去重后的涉及用户数量。 */
    user_count: number;
    /** 次数类资源扣减减去返还后的净消耗。 */
    count_net_usage: number;
    /** Credit 类资源扣减减去返还后的净消耗。 */
    credit_net_usage: number;
}

/** 管理端用量汇总接口的响应数据。 */
export interface ApiUsageStatisticsSummaryResponse {
    /** 按任务分类汇总的列表；灰度期间旧版后端可能不返回。 */
    task_categories?: UsageTaskCategorySummary[];
    /** 按资源 Key 稳定排序的资源汇总列表。 */
    resources: UsageResourceSummary[];
}

/** 一次原始扣减或返还事件。 */
export interface UsageStatisticsDetail {
    /** 用量事件主键。 */
    pk: string;
    /** 事件所属用户 ID。 */
    user_id: string;
    /** Identity 当前返回的用户展示名；无法查询时为 `null`。 */
    user_name: string | null;
    /** 消耗发生时记录的公司 ID；无法回填的历史数据为 `null`。 */
    company_id: string | null;
    /** Identity 当前返回的公司名；无法查询时为 `null`。 */
    company_name: string | null;
    /** 关联任务 ID。 */
    task_id: string;
    /** 归并后的任务分类。 */
    task_category: UsageTaskCategory;
    /** 后端记录的原始任务类型。 */
    task_type: string;
    /** 后端资源 Key。 */
    resource_name: string;
    /** 后端返回的资源展示名。 */
    resource_display_name: string;
    /** 资源计量类型。 */
    resource_type: UsageResourceType;
    /** 按优先级归一后的消费类型。 */
    consumption_type: UsageConsumptionType;
    /** 本次事件的原始用量值。 */
    usage_value: number;
    /** 根据用量值符号判定的扣减或返还类型。 */
    operation_type: UsageOperationType;
    /** 关联的套餐 ID；不存在时为 `null`。 */
    package_id: string | null;
    /** 事件创建时间，Unix 毫秒时间戳。 */
    created_at: number;
}

/** 管理端用量明细接口的分页响应数据。 */
export interface ApiUsageStatisticsDetailsResponse {
    /** 当前页原始用量事件。 */
    list: UsageStatisticsDetail[];
    /** 符合条件的事件总数。 */
    total: number;
    /** 当前页码。 */
    page: number;
    /** 每页条数。 */
    limit: number;
    /** 总页数。 */
    totalPage: number;
}

/** Identity 租户公司搜索候选。 */
export interface UsageCompanyOption {
    /** Identity 公司 ID。 */
    company_id: string;
    /** Identity 公司名称。 */
    company_name: string;
}

/** 管理端用量统计 API 的依赖注入标识。 */
export const UsageStatisticsApi = Symbol('UsageStatisticsApi');

/** 管理端用量统计 API。 */
export interface UsageStatisticsApi {
    /** 查询用户当前的九项实时账户额度。 */
    getUsageAccountQuotas(data: ApiUsageAccountQuotaRequest): Promise<ApiUsageAccountQuotaResponse>;
    /** 查询按资源维度聚合的用量汇总。 */
    getUsageStatisticsSummary(data: ApiUsageStatisticsFilter): Promise<ApiUsageStatisticsSummaryResponse>;
    /** 查询原始扣减或返还事件明细。 */
    getUsageStatisticsDetails(data: ApiUsageStatisticsDetailsRequest): Promise<ApiUsageStatisticsDetailsResponse>;
    /** 按公司名称前缀搜索 Identity 租户公司。 */
    searchUsageCompanies(companyName: string): Promise<UsageCompanyOption[]>;
}
